import React from 'react';
import PropTypes from 'prop-types';
import { TrendingUp } from 'lucide-react';
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatCurrency, formatShortCurrency } from '../../utils/valuationDashboardUtils';

/**
 * ValuationForecastChart - Displays valuation growth forecast
 * Shows projected business value based on growth rate assumptions
 */
const ValuationForecastChart = ({
  forecastData,
  growthRate,
  latestValuation
}) => {
  return (
    <div className="bg-white rounded-xl shadow-md p-3 sm:p-5 border border-gray-100 hover:shadow-lg transition-all duration-300">
      <div className="mb-3 sm:mb-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="p-1.5 sm:p-2 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg">
            <TrendingUp className="text-blue-600" size={16} />
          </div>
          <h2 className="text-base sm:text-lg font-bold text-gray-900">Business Exit Value Growth Forecast</h2>
        </div>
        <p className="text-xs text-gray-500 pl-8 sm:pl-11">
          Projected at {growthRate}% annual growth rate
        </p>
      </div>

      <ResponsiveContainer width="100%" height={280} className="sm:h-80">
        <AreaChart data={forecastData}>
          <defs>
            <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.5}/>
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
            </linearGradient>
            <linearGradient id="bottomGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#ffffff" stopOpacity={1} />
              <stop offset="95%" stopColor="#ffffff" stopOpacity={1} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 10 }}
            stroke="#9ca3af"
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fontSize: 10 }}
            stroke="#9ca3af"
            tickFormatter={(value) => formatShortCurrency(value)}
            width={60}
          />

          <Tooltip
            formatter={(value) => formatCurrency(value)}
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '12px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}
            itemStyle={{ padding: '4px 0' }}
            labelStyle={{ fontWeight: 'bold', marginBottom: '8px' }}
            itemSorter={(item) => {
              const order = {
                high: 1,
                value: 2,
                low: 3,
              };
              return order[item.dataKey] || 999;
            }}
          />

          {/* Range area for projected valuation */}
          <Area
            type="monotone"
            dataKey="high"
            stroke="black"
            strokeWidth={0}
            name="High"
            stackID="low"
            fill="url(#colorValue)"
            fillOpacity={0.6}
          />
          <Area
            type="monotone"
            dataKey="low"
            stroke="black"
            strokeWidth={0}
            stackID="low"
            name="Low"
            fill="url(#bottomGradient)"
            fillOpacity={1}
          />

          {/* Main projection line - rendered last to appear on top */}
          <Line
            type="monotone"
            dataKey="value"
            stroke="#3b82f6"
            strokeWidth={3}
            dot={{ fill: '#3b82f6', r: 4 }}
            name="Median Value"
            style={{ zIndex: 1000 }}
          />
        </AreaChart>
      </ResponsiveContainer>

      {/* Custom Legend with Tooltips */}
      <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-200">
        <div className="flex flex-wrap gap-2 sm:gap-4 justify-center text-xs">
          {/* Projected Value */}
          <div className="group relative">
            <div className="flex items-center gap-1 sm:gap-1.5 cursor-help">
              <div className="w-4 sm:w-6 h-0.5" style={{ backgroundColor: '#3b82f6' }}></div>
              <span className="text-gray-700 font-medium text-xs">Median</span>
            </div>
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
              Your business valuation forecast based on growth assumptions
            </div>
          </div>

          {/* Valuation Range */}
          <div className="group relative">
            <div className="flex items-center gap-1 sm:gap-1.5 cursor-help">
              <div className="w-4 sm:w-6 h-2 sm:h-3 rounded" style={{ background: 'linear-gradient(to bottom, rgba(59, 130, 246, 0.6) 0%, rgba(59, 130, 246, 0.6) 100%)' }}></div>
              <span className="text-gray-700 font-medium text-xs">Range</span>
            </div>
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
              Valuation uncertainty range (±25%)
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
        <p className="text-xs text-blue-900 font-medium">
          <span className="font-bold">Note:</span> Growth projection starts from current {latestValuation?.method?.toUpperCase() || 'valuation'} of {formatCurrency(latestValuation?.valuation_amount || 0)}
        </p>
      </div>
    </div>
  );
};

ValuationForecastChart.propTypes = {
  forecastData: PropTypes.arrayOf(PropTypes.object).isRequired,
  growthRate: PropTypes.number.isRequired,
  latestValuation: PropTypes.object
};

export default ValuationForecastChart;
